// Speediance client (V1.1).
//
// V1 had this module talking directly to the speediance-proxy Worker from the
// browser, with per-device credentials (proxy URL + API key) in localStorage.
// V1.1 routes all proxy traffic through protocol-api via its Service Binding,
// so there are no per-device creds: this module only constructs requests
// against `/api/speediance/library` and `/api/speediance/push` on the
// already-authenticated protocol-api Worker.
//
// `formatSpeedianceError` stays — protocol-api preserves the proxy's `error`
// kind on the response envelope, so the renderer keeps switching on the same
// taxonomy.

import { apiClient, ApiError, ApiNetworkError } from './api';

// ---- AbortSignal.timeout polyfill (Safari <17.4) ----
export function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const c = new AbortController();
  setTimeout(() => c.abort(new DOMException('TimeoutError', 'TimeoutError')), ms);
  return c.signal;
}

// ---- Speediance library entry shape (returned by /library and /catalog) ----

export type SpeedianceVariant = {
  id: number;
  name: string;
  // Present on the D1 catalog shape; absent on the legacy proxy shape.
  coachLanguage?: string | null;
  imageUrl?: string | null;
  [k: string]: unknown;
};

export type SpeedianceLibraryEntry = {
  id: number;
  name: string;
  isUnilateral?: boolean;
  variants: SpeedianceVariant[];
  // Rich fields present on the D1 catalog shape (undefined on legacy proxy).
  category?: string | null;
  mainMuscle?: string | null;
  imageUrl?: string | null;
  context?: string | null;
  [k: string]: unknown;
};

// ---- Catalog (D1-backed, routed through protocol-api) ----

// Reads the persisted exercise catalog from D1 (synced via
// /api/speediance/library/sync). Replaces the live proxy /library call as the
// mapping picker's source — fast, offline-tolerant, and carries the rich
// fields (image, muscle, form cue) the picker now renders.
export async function catalogLibrary(): Promise<SpeedianceLibraryEntry[]> {
  try {
    const data = await apiClient.get<{ library?: SpeedianceLibraryEntry[] }>(
      '/api/speediance/catalog',
    );
    return Array.isArray(data?.library) ? data.library : [];
  } catch (err) {
    throw mapApiError(err);
  }
}

// Triggers a fresh catalog sync from Speediance → D1. Returns the counts.
export async function syncCatalog(): Promise<{
  device_type: number;
  exercises_synced: number;
  variants_synced: number;
  synced_at: number;
}> {
  try {
    return await apiClient.post('/api/speediance/library/sync', undefined);
  } catch (err) {
    throw mapApiError(err);
  }
}

// ---- Push (per-session) ----

export type SpeedianceSetPayload = {
  reps: number;
  weight: number;
  rest: number;
  mode: number;
};

export type SpeedianceExercisePayload = {
  groupId: number;
  variantId: number;
  sets: SpeedianceSetPayload[];
};

export type SpeedianceSessionPayload = {
  name: string;
  exercises: SpeedianceExercisePayload[];
  templateId?: number;
};

export type SpeediancePushResult = {
  templateId: number;
  code?: string;
  warning?: string;
};

export async function proxyPush(
  payload: SpeedianceSessionPayload,
): Promise<SpeediancePushResult> {
  let data: { templateId?: number; code?: string; warning?: string };
  try {
    data = await apiClient.post<{ templateId?: number; code?: string; warning?: string }>(
      '/api/speediance/push',
      payload,
    );
  } catch (err) {
    throw mapApiError(err);
  }
  if (typeof data.templateId !== 'number') {
    throw new ProxyError('speediance', 'Worker returned no templateId.');
  }
  return {
    templateId: data.templateId,
    code: data.code,
    warning: data.warning,
  };
}

// Translate the protocol-api error layer back into ProxyError so the existing
// renderer (formatSpeedianceError) keeps working. The proxy's `error` kind
// passes through protocol-api verbatim — see speediance-proxy-routes.ts.
function mapApiError(err: unknown): ProxyError {
  if (err instanceof ApiNetworkError) {
    return new ProxyError('network', err.message);
  }
  if (err instanceof ApiError) {
    return new ProxyError((err.kind as ProxyErrorKind) || 'default', err.detail || err.kind);
  }
  if (err instanceof Error) return new ProxyError('default', err.message);
  return new ProxyError('default', String(err));
}

// ---- Structured error ----

export type ProxyErrorKind =
  | 'network'
  | 'auth_proxy'
  | 'auth_speediance'
  | 'unknown_variant'
  | 'unknown_group'
  | 'template_not_found'
  | 'version_mismatch'
  | 'validation_error'
  | 'validation_local'
  | 'speediance'
  | 'speediance_proxy_auth'
  | 'speediance_proxy_forbidden_origin'
  | 'speediance_proxy_timeout'
  | 'speediance_proxy_failure'
  | 'aborted'
  | 'forbidden_origin'
  | 'cors_not_configured'
  | 'default';

export class ProxyError extends Error {
  readonly kind: ProxyErrorKind;
  constructor(kind: ProxyErrorKind, message: string) {
    super(message);
    this.name = 'ProxyError';
    this.kind = kind;
  }
}

// ---- formatSpeedianceError — ported from protocol.html, same taxonomy ----

export type SpeedianceErrorActions = {
  retry?: () => void;
  retryAsNew?: () => void;
  remap?: () => void;
  reconnect?: () => void;
};

export type FormattedSpeedianceError = {
  copy: string;
  action: { label: string; handler: () => void } | null;
};

export function formatSpeedianceError({
  kind,
  message,
  sessionName,
  actions,
}: {
  kind: ProxyErrorKind | string;
  message?: string;
  sessionName?: string;
  actions?: SpeedianceErrorActions;
}): FormattedSpeedianceError {
  const a = actions || {};
  switch (kind) {
    case 'network':
      return {
        copy:
          'Cannot reach the Protocol API. Check your connection and try again.',
        action: a.retry ? { label: 'RETRY', handler: a.retry } : null,
      };
    case 'auth_proxy':
    case 'speediance_proxy_auth':
      return {
        copy:
          'Protocol API rejected the proxy key. Re-set SPEEDIANCE_PROXY_API_KEY on the protocol-api Worker.',
        action: null,
      };
    case 'auth_speediance':
      return {
        copy:
          'Speediance re-authentication failed. The proxy could not log in — check SPEEDIANCE_EMAIL/PASSWORD secrets in Wrangler.',
        action: null,
      };
    case 'speediance_proxy_timeout':
      return {
        copy: 'Timed out reaching the Speediance proxy. Try again in a moment.',
        action: a.retry ? { label: 'RETRY', handler: a.retry } : null,
      };
    case 'speediance_proxy_forbidden_origin':
      return {
        copy:
          'Service Binding rejected by proxy (403). Verify protocol-api wrangler.toml [[services]] block.',
        action: null,
      };
    case 'speediance_proxy_failure':
      return {
        copy: `Speediance proxy failed: ${message || 'unknown reason'}.`,
        action: a.retry ? { label: 'RETRY', handler: a.retry } : null,
      };
    case 'unknown_variant':
    case 'unknown_group':
      return {
        copy:
          'The mapped exercise variant no longer exists in Speediance. Re-open Edit mapping and re-pick.',
        action: a.remap ? { label: 'RE-MAP', handler: a.remap } : null,
      };
    case 'template_not_found':
      return {
        copy: `Speediance template ${sessionName ? `for ${sessionName} ` : ''}no longer exists. Push will create a new one.`,
        action: a.retryAsNew ? { label: 'CLEAR + RETRY', handler: a.retryAsNew } : null,
      };
    case 'version_mismatch':
      return {
        copy:
          'Speediance rejected the client version. Bump SPEEDIANCE_VERSIONCODE in Wrangler and redeploy — see speediance-proxy/README.md.',
        action: null,
      };
    case 'validation_error':
    case 'validation_local':
      return {
        copy: `Push payload was rejected: ${message || 'unknown reason'}.`,
        action: a.retry ? { label: 'RETRY', handler: a.retry } : null,
      };
    case 'speediance':
      return {
        copy: `Speediance returned an error: ${message || 'unknown reason'}. The template may not have been saved.`,
        action: a.retry ? { label: 'RETRY', handler: a.retry } : null,
      };
    case 'aborted':
      return {
        copy: message || 'Aborted because a previous session failed authentication.',
        action: null,
      };
    default:
      return {
        copy: `Unexpected error: ${message || 'unknown'}. Push not completed.`,
        action: a.retry ? { label: 'RETRY', handler: a.retry } : null,
      };
  }
}
